import { useTranslation } from 'react-i18next';

import { cn } from '@nucleus-commerce/ui/lib/utils';

import { StatusDot, type StatusTone } from '@/shared/ui/status-dot';

import type { ApiStatus, CheckState, LiveState } from '../api/status';
import { useApiStatus } from '../hooks/use-api-status';

const LIVE_TONE: Record<LiveState, StatusTone> = { up: 'success', down: 'destructive' };
const CHECK_TONE: Record<CheckState, StatusTone> = {
  ok: 'success',
  failed: 'destructive',
  unknown: 'muted',
};

function dots(status: ApiStatus | undefined, t: (key: string) => string) {
  if (!status) {
    return [{ key: 'api', tone: 'muted' as StatusTone, label: t('line.checking'), pulse: true }];
  }
  return [
    { key: 'api', tone: LIVE_TONE[status.live], label: t(`live.${status.live}`), pulse: false },
    {
      key: 'postgres',
      tone: CHECK_TONE[status.checks.postgres],
      label: t('checks.postgres'),
      pulse: false,
    },
    { key: 'redis', tone: CHECK_TONE[status.checks.redis], label: t('checks.redis'), pulse: false },
  ];
}

/** One row of live dots: API, Postgres, Redis. Used in the landing footer and the sidebar. */
export function ApiStatusLine({ className }: { className?: string }) {
  const { t } = useTranslation('system');
  const { data } = useApiStatus();
  return (
    <div
      className={cn(
        'text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1 text-xs',
        className,
      )}
      role="status"
      aria-live="polite"
    >
      {dots(data, t).map((dot) => (
        <StatusDot key={dot.key} tone={dot.tone} pulse={dot.pulse} className="text-xs">
          {dot.label}
        </StatusDot>
      ))}
    </div>
  );
}
